import { buildCost } from '../core/editing';
import type { GateCondition } from '../core/gate-condition';
import { CREATION_KINDS } from './creation-kinds';
import type { BuildKind } from './subject-kind';

/** Chosen so the "slag costs money" lesson (`slag-reaches-subscriber`) lands more than once before
 * `filter` arrives to fix it — one bad sale could be a fluke, two is a pattern. */
export const FILTER_UNLOCK_SLAG_SALE_COUNT = 2;

/**
 * Chosen so the merge actually has to have been felt: running carbon to the Subscriber at all means
 * both streams are live and sharing one pipe, which is the pain `zip` answers. The cash branch keeps
 * a player who never sells raw carbon from stalling out.
 */
export const ZIP_UNLOCK_CARBON_SALE_COUNT = 4;

/**
 * Chosen to land well clear of Copper Flats (steel sold ≥ 8), so the two reveals stagger rather than
 * arriving together. `combineLatest` is the contrast to `zip`, and a contrast only reads once the
 * player has actually felt the thing it contrasts with: both halves being spent, steel after steel.
 */
export const COMBINE_LATEST_UNLOCK_STEEL_SALE_COUNT = 16;

export type UnlockableToolKind = BuildKind;

/**
 * The single source of truth for what unlocks each build-tool button — consumed by both the
 * toolbar (which button to show) and the milestone that nudges the player toward it, so the two
 * can never drift apart the way duplicated `everAffordable(peakCash, …)` checks used to.
 */
export function unlockGateFor(toolKind: UnlockableToolKind): GateCondition {
  switch (toolKind) {
    case 'of':
      return { kind: 'cash', threshold: CREATION_KINDS.of.cost };
    case 'from':
      return { kind: 'flag', flag: 'everPlacedFilter' };
    // Last of the creation operators on purpose: "never completes" only means something once the
    // player has watched `of` and `from` both run dry.
    case 'interval':
      return { kind: 'flag', flag: 'everPlacedFrom' };
    case 'map':
      return { kind: 'cash', threshold: buildCost({ type: 'machine', kind: 'map' }) };
    case 'take':
      return { kind: 'cash', threshold: buildCost({ type: 'machine', kind: 'take' }) };
    // Useless without something to listen to, so it waits for the channel rather than for cash.
    case 'takeUntil':
      return { kind: 'flag', flag: 'everPlacedSubject' };
    case 'filter':
      return { kind: 'production', material: 'slag', count: FILTER_UNLOCK_SLAG_SALE_COUNT };
    // Ungated on purpose, and the only operator that is: selling happens inside the pipe now, so a
    // player with no way to place one has no way to earn anything at all.
    case 'switchMap':
      return { kind: 'cash', threshold: 0 };
    case 'zip':
      return {
        kind: 'any',
        conditions: [
          { kind: 'production', material: 'carbon', count: ZIP_UNLOCK_CARBON_SALE_COUNT },
          { kind: 'cash', threshold: buildCost({ type: 'machine', kind: 'zip' }) },
        ],
      };
    // The whole payoff of a channel is shutting an endless stream down, so it arrives with the
    // endless stream and not a moment before.
    case 'subject':
      return { kind: 'flag', flag: 'everPlacedInterval' };
    case 'combineLatest':
      return {
        kind: 'any',
        conditions: [
          {
            kind: 'production',
            material: 'steel',
            count: COMBINE_LATEST_UNLOCK_STEEL_SALE_COUNT,
          },
          { kind: 'cash', threshold: buildCost({ type: 'machine', kind: 'combineLatest' }) },
        ],
      };
  }
}
